import type { AriStateDay, CalendarDayCell } from '@/shared/api';

export const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
export const MONTHS_DE = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

export interface ResolvedDay {
  date: string;
  inMonth: boolean;
  isPast: boolean;
  isToday: boolean;
  isBooked: boolean;
  rate: number | null;
  stopSell: boolean;
  fromChannex: boolean;
  /** Gesamtwohnung oder ein Zimmer belegt die Nacht; der Kanalmanager bekommt sie geschlossen. */
  closedByFamily?: boolean;
  /** Das Apartment ist selbst gebucht und der Verbund belegt dieselbe Nacht. */
  doubleBooked?: boolean;
}

/** Derselbe Grund wie im Grundkalender. */
export const FAMILY_CLOSED_LABEL = 'Durch Verbund geschlossen';

export function buildMonthGrid(
  cursor: Date,
  ariByDate: Map<string, AriStateDay>,
  bookingsByDate: Map<string, CalendarDayCell>,
  defaultRate: number,
): ResolvedDay[] {
  const firstDow = (firstOfMonth(cursor).getUTCDay() + 6) % 7;
  const start = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth(), 1 - firstDow));
  const days: ResolvedDay[] = [];
  const todayIso = new Date().toISOString().slice(0, 10);
  const todayLocal = new Date(todayIso);
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setUTCDate(d.getUTCDate() + i);
    const iso = d.toISOString().slice(0, 10);
    const inMonth = d.getUTCMonth() === cursor.getUTCMonth();
    const ari = ariByDate.get(iso);
    const isStopSell =
      ari?.stopSell === true || (ari?.availability != null && ari.availability <= 0);
    const cell = bookingsByDate.get(iso);
    const booked = cell?.status === 'booked';
    days.push({
      date: iso,
      inMonth,
      isPast: d < todayLocal,
      isToday: iso === todayIso,
      isBooked: booked,
      rate: ari?.rate ?? defaultRate ?? null,
      stopSell: isStopSell,
      fromChannex: ari?.rate != null,
      closedByFamily: !booked && cell?.occupiedByFamily === true,
      doubleBooked: booked && cell?.occupiedByFamily === true,
    });
    // 6 Wochen reichen — wenn nächster Tag schon ausserhalb des Monats UND Woche voll ist, abbrechen.
    if (days.length >= 35 && days.length % 7 === 0 && !inMonth) {
      break;
    }
  }
  return days;
}

export function firstOfMonth(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

export function addMonths(d: Date, delta: number): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + delta, 1));
}

export function monthRange(cursor: Date): { from: string; to: string } {
  const firstDow = (firstOfMonth(cursor).getUTCDay() + 6) % 7;
  const start = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth(), 1 - firstDow));
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 41);
  return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
}

/**
 * Kurzform für die Tageszelle des Kalenders — 42 Zellen nebeneinander tragen keinen vollen
 * Betrag. Krumme Preise behalten ihre Nachkommastellen: „133€" statt 132,50 € wäre eine
 * falsche Zahl, keine kurze.
 */
export function formatShortRate(rate: number, currency: string): string {
  if (rate >= 1000) return `${(rate / 1000).toFixed(1)}k`;
  const value = Number.isInteger(rate)
    ? String(rate)
    : rate.toFixed(2).replace(/0$/, '').replace('.', ',');
  return currency === 'EUR' ? `${value} €` : value;
}
